<template>
  <div class="auth">
    <div class="auth__inner">
      <div class="bg-light"></div>
      <div class="auth__header">
        <img
          class="auth__logo"
          src="@/assets/images/logo.png"
          alt="Neopay Logo"
        />
        <div class="auth__brand">Neo<span style="color: #b28ee4;">Bit</span></div>
      </div>
      <form
        class="auth__content"
      >
        <div
          class="auth__switcher"
        >
          <button
            class="btn"
            type="button"
            :class="{
              'active': isSignInFields,
            }"
            @click="isSignInFields = true"
          >
            Вход
          </button>
          <button
            class="btn"
            type="button"
            :class="{
              'active': !isSignInFields,
            }"
            @click="isSignInFields = false"
          >
            Регистрация
          </button>
        </div>
        <component
          :key="fieldsKey"
          :is="isSignInFields ? SignInFields : SignUpFields"
          :errors="errors"
          @updatedData="requestBody = {...$event}"
        />
        <div
          v-if="generalErrorMessage"
          class="general-error-msg"
        >
          {{ generalErrorMessage }}
        </div>
        <button
          type="button"
          class="btn auth__submit-btn"
          @click="auth"
        >
          <span>{{ isSignInFields ? 'Войти' : 'Зарегистрироваться' }}</span>
        </button>
      </form>
    </div>
    <AppAlerts
      :alerts="alerts.alerts.value"
      @removeAlert="alerts.removeAlert"
    />
  </div>
</template>

<script setup>
import {
  ref,
  reactive,
  watch,
} from 'vue';
import useAlerts from '@/composables/useAlerts';
import { alertStatus } from '@/const';
import signInRequest from '@/requests/signInRequest';
import signUpRequest from '@/requests/signUpRequest';
import mockSignInRequest from '@/requests/mockSignInRequest';
import mockSignUpRequest from '@/requests/mockSignUpRequest';
import SignInFields from '@/components/SignInFields.vue';
import SignUpFields from '@/components/SignUpFields.vue';
import AppAlerts from '@/components/AppAlerts.vue';

const alerts = useAlerts();
const fieldsKey = ref(Date.now());
const requestBody = ref({
  contact: null,
  email: null,
  password: null,
  passwordConfirm: null,
  captcha: 'stub',
});
const isSignInFields = ref(true);
const generalErrorMessage = ref(null);
const isDev = process.env.NODE_ENV === 'development';

const errors = reactive({
  email: [],
  password: [],
  passwordConfirm: [],
  captcha: [],
});

const resetErrors = () => {
  errors.email = [];
  errors.password = [];
  errors.passwordConfirm = [];
  errors.captcha = [];
  generalErrorMessage.value = null;
};

const onSuccessAuth = () => {
  window.location.reload();
};

const onSuccessSignUp = () => {
  alerts.addAlert(
    'Успешная регистрация!',
    alertStatus.Success,
    [
      `Ожидайте подтверждения аккаунта от администрации чтобы войти в личный кабинет.
      Это может занять некоторое время.`,
      'Сейчас страница обновится.',
    ],
  );

  setTimeout(() => {
    window.location.reload();
  }, 6000);
};

const onFailAuth = (res) => {
  resetErrors();

  if (res.response.status === 400
    && typeof res.response.data === 'object'
    && res.response.data !== null) {
    Object.entries(res.response.data).forEach((error) => {
      const key = error[0];
      const value = error[1];

      if (Array.isArray(errors[key])) {
        errors[key].push(...value);
      }
    });
  } else if (res.response.status === 400
    && typeof res.response.data === 'string') {
    generalErrorMessage.value = res.response.data;
  } else {
    // Другая непредвиденная ошибка.
    alerts.addAlert(
      'Произошла ошибка!',
      alertStatus.Fail,
      [
        'Что-то пошло не правильно. Попробуйте обновить страницу, или обратитесь за помощью в поддержку.',
      ],
    );
  }
};

const isEmptyField = (value, field) => {
  if (!value) errors[field].push('Это поле обязательное.');

  return !value;
};

const checkEmail = () => {
  if (requestBody.value.email
    && !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}/g.test(requestBody.value.email)) {
    errors.email.push('Введите действительный адрес электронной почты.');

    return false;
  }

  return true;
};

const checkSignInValidation = () => {
  let errorCounter = 0;

  errorCounter += Number(isEmptyField(requestBody.value.email, 'email'));
  errorCounter += Number(isEmptyField(requestBody.value.password, 'password'));
  errorCounter += Number(!checkEmail());

  return errorCounter === 0;
};

const checkSignUpValidation = () => {
  let errorCounter = 0;

  errorCounter += Number(isEmptyField(requestBody.value.email, 'email'));
  errorCounter += Number(isEmptyField(requestBody.value.password, 'password'));
  errorCounter += Number(isEmptyField(requestBody.value.passwordConfirm, 'passwordConfirm'));
  errorCounter += Number(!checkEmail());

  if (requestBody.value.password !== requestBody.value.passwordConfirm) {
    errors.passwordConfirm.push('Пароли не совпадают.');
    errorCounter += 1;
  }

  return errorCounter === 0;
};

const auth = () => {
  resetErrors();

  const isValidationPassed = isSignInFields.value
    ? checkSignInValidation()
    : checkSignUpValidation();

  if (!isValidationPassed) return;

  if (isSignInFields.value) {
    if (isDev) mockSignInRequest(false, onSuccessAuth, onFailAuth);
    else signInRequest(requestBody.value, onSuccessAuth, onFailAuth);
  }

  if (!isSignInFields.value) {
    if (isDev) mockSignUpRequest(true, onSuccessSignUp, onFailAuth);
    else signUpRequest(requestBody.value, onSuccessSignUp, onFailAuth);
  }
};

watch(isSignInFields, () => {
  resetErrors();
});
</script>

<style lang="scss">
body {
  background: $bg-default;
  color: #fff;
  font-family: 'Montserrat', Arial, Helvetica, sans-serif;
}

.auth {
  margin: 175px 0px 0px;
  padding: 0px 16px;

  @media screen and (max-width: 1180px) {
    margin: 50px 0px 0px;
  }

  @media screen and (max-width: 767px) {
    margin: 30px 0px 0px;
  }
}

.auth__inner {
  max-width: 454px;
  margin: 0px auto;
}

.auth__header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.auth__logo {
  display: block;
  max-width: 42px;
  flex-shrink: 0;
}

.auth__brand {
  font-family: 'M PLUS Rounded 1c', Arial, Helvetica, sans-serif;
  font-size: 32px;
  font-weight: 800;
  line-height: 36px;
  letter-spacing: .4px;
}

.auth__content {
  box-sizing: border-box;
  background: $bg-second;
  padding: 32px 24px;
  border-radius: 16px;
  border: 1px solid #2E3149;
  margin: 20px 0px 0px;

  @media screen and (max-width: 767px) {
    padding: 24px 16px;
  }
}

.auth__switcher {
  box-sizing: border-box;
  display: flex;
  background: $bg-default;
  padding: 8px;
  border-radius: 12px;
  gap: 8px;
  margin: 0px 0px 12px;

  .btn {
    width: 100%;
    padding: 8px;
    font-weight: 300;
    background-color: transparent;

    &.active {
      background: $bg-accent-v2;
    }

    &:not(:disabled):not(:focus):hover {
      background: #ffffff12;
    }
  }
}

.auth__submit-btn {
  margin: 18px 0px 0px;
  width: 100%;
}

.general-error-msg {
  margin: 14px 0px 0px;
  color: $color-error;
  font-size: 13px;
  line-height: 1.36;
}

.bg-light {
  position: absolute;
  top: 10px;
  left: 50%;
  transform: translateX(-50%);
  width: 755px;
  height: 612px;
  background: url('@/assets/images/light.png') no-repeat center / contain;
  z-index: -1;

  @media screen and (max-width: 1024px) {
    top: -140px;
    left: 50%;
  }
}
</style>
